import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Menu, User, Settings, Sun, Moon, ChevronRight, LogOut, Search, Bell, X } from 'lucide-react'
import { useMotivatorNotifications } from '../../hooks/useMotivatorNotifications'
import type { RootState } from '../../store'
import { logout } from '../../store/slices/authSlice'
import { AuthService } from '../../services/authService'
import { useTheme } from '../providers/ThemeProvider'
import { useBranding } from '../../hooks/useBranding'
import { GlobalSearch } from '../search/GlobalSearch'
import { DEFAULT_APP_HOME } from '../../config/routes'
import { ServerPacificTime } from './ServerPacificTime'
import { isNavigableRoute } from './routeTitles'

/**
 * Icon-only controls in the header. 44px is the touch floor; the header's own
 * height is user-tunable, so the buttons centre their icon inside the floor
 * rather than growing by padding (2026-08-29).
 *
 * Each site supplies its own display utility, so `hidden sm:inline-flex` is
 * never fighting a base `inline-flex` in the same class list.
 */
const ICON_BUTTON =
  'items-center justify-center min-h-touch min-w-touch rounded-md transition-colors hover:bg-surface-alt'

/**
 * Alert time in the reader's own timezone. Server stamps are UTC, and a naive
 * one ("2026-08-29T14:03:00", no Z / offset) is parsed by browsers as LOCAL
 * time — which silently shifted every bell time by the UTC offset (2026-08-29).
 */
function notifStamp(value: string): string {
  if (!value) return ''
  const trimmed = value.trim()
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)
  const d = new Date(hasZone ? trimmed : `${trimmed.replace(' ', 'T')}Z`)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

interface HeaderProps {
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
  mobileSidebarOpen: boolean
  setMobileSidebarOpen: (open: boolean) => void
}

export function Header({ sidebarOpen, setSidebarOpen, mobileSidebarOpen, setMobileSidebarOpen }: HeaderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user } = useSelector((state: RootState) => state.auth)
  const { isDark, isDiary, toggleDarkMode } = useTheme()
  const branding = useBranding()
  const authService = new AuthService()
  const [searchOpen, setSearchOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const bellRef = useRef<HTMLButtonElement>(null)
  const { notifications, unreadCount, markAsRead, markAllAsRead, fetchNotifications } = useMotivatorNotifications(60000)

  const closeNotifs = () => {
    setNotifOpen(false)
    bellRef.current?.focus()
  }

  /* 2026-08-29: the bell's dropdown was an unmanaged popover — Escape did
     nothing and focus was stranded on a control that no longer existed once
     it closed. */
  useEffect(() => {
    if (!notifOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        closeNotifs()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [notifOpen])

  // Global keyboard shortcut for search (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleLogout = async () => {
    try {
      await authService.logout()
      dispatch(logout())
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
      // Still logout locally even if server logout fails
      dispatch(logout())
      navigate('/login')
    }
  }

  const toggleMobileMenu = () => {
    setMobileSidebarOpen(!mobileSidebarOpen)
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  // Get current page info for breadcrumb
  const getCurrentPageInfo = () => {
    const pathnames = location.pathname.split('/').filter((x) => x)
    const currentPath = pathnames[pathnames.length - 1] || 'dashboard'
    const pageName = currentPath.charAt(0).toUpperCase() + currentPath.slice(1).replace(/-/g, ' ')
    return { pageName, pathnames }
  }

  // Simple breadcrumb logic. Colour comes from semantic tokens (now
  // re-pointed in diary). The only diary branches left are copy and the
  // handwritten current-page name — those are character, not workarounds.
  const sep = isDiary ? '›' : '/'
  const getBreadcrumb = () => {
    const { pathnames } = getCurrentPageInfo()
    
    if (sidebarOpen) {
      return (
        <nav 
          className={`flex text-secondary ${isDiary ? 'diary-serif' : ''}`}
          aria-label="Breadcrumb"
        >
          <Link 
            to={DEFAULT_APP_HOME} 
            className="text-secondary hover:text-primary"
          >
            {isDiary ? '📖 Home' : 'Home'}
          </Link>
          {pathnames.map((name, index) => {
            const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`
            const isLast = index === pathnames.length - 1
            const displayName = name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ')
            /* 2026-08-29: every path prefix used to be a link, including ones
               that are not routes (/habits/occurrences, /my-people/person,
               /dev), which landed the reader on a 404. A crumb with nothing
               behind it is plain text now. */
            const linkable = !isLast && isNavigableRoute(routeTo)
            return (
              <span key={name} className="flex items-center">
                <span className="mx-2 text-secondary">{sep}</span>
                {linkable ? (
                  <Link
                    to={routeTo}
                    className="text-secondary hover:text-primary"
                  >
                    {displayName}
                  </Link>
                ) : (
                  <span className={isLast
                    ? `text-primary font-medium ${isDiary ? 'diary-handwritten' : ''}`
                    : 'text-secondary'}
                  >
                    {displayName}
                  </span>
                )}
              </span>
            )
          })}
        </nav>
      )
    }

    return (
      <div className="flex items-center space-x-2">
        <button
          onClick={toggleSidebar}
          className="flex items-center space-x-2 p-2 min-h-touch rounded-lg transition-colors hover:bg-surface-alt"
          title="Open sidebar"
          aria-label="Open sidebar"
        >
          <img src={branding.icon} alt={branding.name} className="w-8 h-8 rounded-lg" />
          <ChevronRight className="h-4 w-4 text-secondary" />
        </button>
        <div className="flex items-center space-x-2 text-secondary">
          <span>{sep}</span>
          <span className={`font-medium text-primary ${isDiary ? 'diary-handwritten' : ''}`}>
            {getCurrentPageInfo().pageName}
          </span>
        </div>
      </div>
    )
  }

  return (
    <header className="app-header flex items-center justify-between px-3 sm:px-6 border-b bg-surface border-soft">
      {/* Left side */}
      <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
        {/* Mobile menu button */}
        <button 
          onClick={toggleMobileMenu}
          className={`inline-flex lg:hidden ${ICON_BUTTON}`}
          data-testid="mobile-menu-button"
          aria-label="Open mobile menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        
        {/* Breadcrumb */}
        <div className="hidden sm:block">
          {getBreadcrumb()}
        </div>
      </div>
      
      {/* Center - Search Bar */}
      <div className="flex-1 max-w-md mx-4 hidden sm:block">
        <button
          onClick={() => setSearchOpen(true)}
          className="app-search w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors text-muted bg-surface-alt hover:bg-soft border-soft"
        >
          <Search className="h-4 w-4 flex-shrink-0" />
          <span className={`flex-1 text-left ${isDiary ? 'diary-serif' : ''}`}>
            {isDiary ? 'Search your journal...' : 'Search data & help...'}
          </span>
          <div className="flex items-center gap-0.5 text-xs text-secondary">
            <kbd className="app-kbd px-1.5 py-0.5 rounded font-sans">⌘</kbd>
            <kbd className="app-kbd px-1.5 py-0.5 rounded font-sans">K</kbd>
          </div>
        </button>
      </div>

      {/* Right side */}
      <div className="flex items-center space-x-1 sm:space-x-3 flex-shrink-0">
        <div className="hidden xl:block">
          <ServerPacificTime />
        </div>

        {/* Mobile search button */}
        <button
          onClick={() => setSearchOpen(true)}
          className={`inline-flex sm:hidden ${ICON_BUTTON}`}
          title="Search"
          aria-label="Search"
        >
          <Search className="h-5 w-5" />
        </button>

        {/* Notification bell */}
        <div className="relative">
          <button
            ref={bellRef}
            onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen) fetchNotifications(); }}
            /* The double-click shortcut to the full page is gone (2026-09-06):
               nobody discovers a double click, and the panel's own "See all"
               link is the honest way there. */
            className={`relative inline-flex ${ICON_BUTTON}`}
            title="Notifications"
            aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
            aria-haspopup="true"
            aria-expanded={notifOpen}
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 flex items-center justify-center text-app-micro font-bold text-on-brand bg-brand rounded-full">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1.5rem)] bg-surface rounded-xl shadow-xl border border-soft z-50 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-soft">
                <h3 className="font-semibold text-sm text-primary">Notifications</h3>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="inline-flex items-center min-h-touch px-1 text-xs text-brand hover:underline"
                    >
                      Mark all read
                    </button>
                  )}
                  <button
                    onClick={closeNotifs}
                    aria-label="Close notifications"
                    className="inline-flex items-center justify-center min-h-touch min-w-touch rounded-md text-muted hover:text-secondary hover:bg-surface-alt"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-secondary text-sm">
                    No notifications yet
                  </div>
                ) : (
                  notifications.slice(0, 10).map((n) => (
                    <button
                      key={n.id}
                      onClick={() => markAsRead(n.id)}
                      className={`w-full text-left px-4 py-3 border-b border-soft/50 hover:bg-surface-alt transition-colors ${
                        !n.read ? 'bg-brand-subtle/50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.read && (
                          <span className="mt-1.5 h-2 w-2 rounded-full bg-brand flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-snug ${!n.read ? 'font-medium text-primary' : 'text-secondary'}`}>
                            {n.message}
                          </p>
                          <p className="text-xs text-muted mt-1">
                            {notifStamp(n.created_at)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
              {/* Double-click on the bell is undiscoverable on its own, so the
                  history page also has a plain link here (2026-08-29). */}
              <Link
                to="/notifications"
                onClick={() => setNotifOpen(false)}
                className="block px-4 py-2.5 text-center text-sm font-medium text-brand border-t border-soft hover:bg-surface-alt transition-colors"
              >
                See all notifications
              </Link>
            </div>
          )}
        </div>

        {/* Dark mode toggle */}
        <button 
          onClick={toggleDarkMode}
          className={`hidden sm:inline-flex ${ICON_BUTTON}`}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>

        {/* User menu */}
        <div className="flex items-center space-x-1 sm:space-x-2">
          <Link to="/profile" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            {user?.avatar ? (
              <img 
                src={user.avatar} 
                alt={user.username || 'User avatar'}
                className="h-8 w-8 rounded-full object-cover border-2 border-soft"
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-brand flex items-center justify-center">
                <User className="h-4 w-4 text-on-brand" />
              </div>
            )}
            <div className="hidden md:block">
              <p className={`text-sm font-medium text-primary ${isDiary ? 'diary-handwritten' : ''}`}>
                {user?.preferredName || user?.username || 'User'}
              </p>
              <p className="text-xs text-secondary">
                {user?.email || 'user@example.com'}
              </p>
            </div>
          </Link>
          
          {/* User dropdown */}
          <div className="relative flex items-center space-x-1 sm:space-x-2">
            <Link 
              to="/profile" 
              className={`hidden sm:inline-flex text-secondary ${ICON_BUTTON}`}
              title="Profile Settings"
              aria-label="Profile settings"
            >
              <Settings className="h-4 w-4" />
            </Link>
            <button
              onClick={handleLogout}
              className={`inline-flex text-secondary ${ICON_BUTTON}`}
              title="Logout"
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Global Search Modal */}
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  )
}
